/**
 * A caption laid on a picture: dragged, turned and pinched into place.
 *
 * The placement is stored rather than burned into the photograph. Burning it
 * in is what a phone does when it exports, and it would mean re-encoding the
 * image on the phone, losing the text to whatever the JPEG did to it, and
 * having no way to fix a typo. Kept apart, the words stay as sharp as the
 * screen can draw them and can still be moved tomorrow.
 */
import { CAPTION_STYLE_DEFAULT, type CaptionStyle } from '../../shared/captionStyle';
import type { CaptionAt } from '../../shared/types';

/** Where a caption starts before anybody moves it: the middle, upright. */
export const CAPTION_HOME: CaptionAt = { x: 0.5, y: 0.5, rotate: 0, scale: 1 };

export function captionAtOf(at: CaptionAt | undefined): CaptionAt {
  return at ?? CAPTION_HOME;
}

/** The transform that puts a caption where it belongs, at any frame size. */
export function styleFor(at: CaptionAt): React.CSSProperties {
  return {
    left: `${at.x * 100}%`,
    top: `${at.y * 100}%`,
    transform: `translate(-50%, -50%) rotate(${at.rotate}deg) scale(${at.scale})`,
    // A width somebody chose by dragging an edge. Without one the box is as
    // wide as its words (the stylesheet's max-content), never as wide as the
    // room left beside it -- which moved as it was dragged.
    ...(at.width !== undefined ? { width: `${at.width * 100}%`, maxWidth: 'none' } : {}),
  };
}

/**
 * The classes that give words their face and colour. The colours themselves
 * live in the stylesheet beside the faces: black words get a light wash
 * behind them rather than the dark one, or they would vanish into it.
 */
export function lookClasses(look: CaptionStyle | undefined): string {
  const { font, color } = look ?? CAPTION_STYLE_DEFAULT;
  return `caption--${font} caption--${color}`;
}

/** A caption as everybody else sees it: in place, and not going anywhere. */
export function Caption({ text, at, look }: { text: string; at?: CaptionAt; look?: CaptionStyle }) {
  if (!text) return null;
  return (
    <span className={`caption ${lookClasses(look)}`} style={styleFor(captionAtOf(at))}>
      {text}
    </span>
  );
}
